import {AttrsSchema, Component} from 'panel';
import throttle from 'lodash/throttle';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {Persistence} from 'common/report/persistence';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {getReportName} from 'common/report/util';
import {SectionItem} from 'common/components/items-menu/types';

import type {SidenavAttrs, SidenavCta, SidenavSection, SidenavSectionItem, SidenavState} from './types';
import {
  DropPosition,
  SIDENAV_COLLAPSED_WIDTH,
  SIDENAV_DEFAULT_WIDTH,
  SIDENAV_MAX_WIDTH,
  SIDENAV_MIN_WIDTH,
  SIDENAV_RESIZE_SNAP_WINDOW,
  TOGGLE_COLLAPSE_EVENT,
  TrackingEvent,
} from './constants';
import {findSidenavIndices, moveSidenavItem} from './util';
import {template} from './template';
import css from './index.styl?inline';

export interface SidenavJsxProps {
  attrs: SidenavAttrs;
  on: {
    select?: (ev: CustomEvent<SidenavSectionItem>) => void;
    sidenavResize?: (ev: CustomEvent<{width: number}>) => void;
    headerAction?: (ev: CustomEvent<{value: string}>) => void;
    footerAction?: (ev: CustomEvent<{value: string}>) => void;
    dragDrop?: (ev: CustomEvent<SidenavSection[]>) => void;
    overflowMenuSelect?: (ev: CustomEvent<{item: SidenavSectionItem; menuItem: SectionItem}>) => void;
  };
  props?: {
    confirmNavigateFunc?: () => boolean;
  };
}

const COMPONENT_NAME = `mp-sidenav`;

export class Sidenav extends Component<SidenavState, SidenavAttrs> {
  private sectionsEl: HTMLDivElement;
  private persistence: Persistence;
  private collapseMediaQuery: MediaQueryList;
  private collapseMediaQueryListener: (ev: MediaQueryListEvent) => void;
  public confirmNavigateFunc?: () => boolean;

  static get attrsSchema(): AttrsSchema<SidenavAttrs> {
    return {
      sections: {type: `json`, description: `interface: SidenavSection[]`, tsType: `SidenavSection[]`},
      selected: {type: `string`, description: `value of selected item`},
      loading: {type: `boolean`},
      'min-adjacent-width': {
        type: `number`,
        description: `minimum width for the horizontally adjacent content on the page. used to block resizing and collapsing the sidenav when necessary.`,
      },
      header: {type: `json`, tsType: `SidenavHeader`},
      'footer-actions': {type: `json`, tsType: `SidenavCta[]`, default: []},
      'dragdrop-enabled': {type: `boolean`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        activeDragSectionIdx: null,
        canExpand: true,
        collapsedSectionSet: new Set<string>(),
        isCollapsed: false,
        isHovering: false,
        isOverflowMenuOpen: false,
        isResizing: false,
        isScrolling: false,
        sections: [],
        selectedItem: null,
        selectedSectionIndex: null,
        width: SIDENAV_DEFAULT_WIDTH,
      },
      helpers: {
        onSelectItem: (ev) => {
          this.dispatchEvent(new CustomEvent(`select`, {detail: ev.detail}));
        },

        getSelectedIndices: () => {
          return findSidenavIndices(this.attr(`sections`), (_, item) => {
            return item.value === this.attr(`selected`);
          });
        },

        toggleSection: (sectionLabel: string) => {
          const collapsedSectionSet = new Set([...this.state.collapsedSectionSet]);
          const isCollapsed = collapsedSectionSet.has(sectionLabel);
          if (isCollapsed) {
            collapsedSectionSet.delete(sectionLabel);
          } else {
            collapsedSectionSet.add(sectionLabel);
          }

          this.track(isCollapsed ? TrackingEvent.ExpandedSection : TrackingEvent.CollapsedSection);
          this.update({collapsedSectionSet});
        },

        onSectionsInserted: (node) => {
          this.sectionsEl = node.elm;
          this.sectionsEl.addEventListener(
            `scroll`,
            throttle(() => {
              if (
                (!this.state.isScrolling && this.sectionsEl.scrollTop > 0) ||
                (this.state.isScrolling && this.sectionsEl.scrollTop === 0)
              ) {
                this.update({isScrolling: this.sectionsEl.scrollTop !== 0});
              }
            }, 100),
          );
        },

        startResizing: (ev: MouseEvent) => {
          if (ev.button !== 0) {
            return;
          }
          ev.preventDefault();

          this.update({isResizing: true});

          const {width} = this.state;
          const resizeHandler = (mouseMoveEvent: MouseEvent) => this.onSidenavResize(ev.clientX, width, mouseMoveEvent);

          const stopResizingHandler = () => {
            window.removeEventListener(`mousemove`, resizeHandler);
            window.removeEventListener(`mouseup`, stopResizingHandler);
            this.update({isResizing: false});
            this.setPersistedState({width: this.state.width});
            this.track(TrackingEvent.Resize, {width: this.state.width});
          };

          window.addEventListener(`mousemove`, resizeHandler);
          window.addEventListener(`mouseup`, stopResizingHandler);
        },

        getFooterActions: () => {
          if (!this.state.canExpand) {
            return this.attr(`footer-actions`);
          }

          const toggleCollapseCta: SidenavCta = this.state.isCollapsed
            ? {label: `Expand`, icon: `push-right`, value: TOGGLE_COLLAPSE_EVENT}
            : {label: `Collapse`, icon: `push-left`, value: TOGGLE_COLLAPSE_EVENT};
          return [...this.attr(`footer-actions`), toggleCollapseCta];
        },

        onFooterActionClick: (value: string) => {
          if (value === TOGGLE_COLLAPSE_EVENT) {
            const isCollapsed = !this.state.isCollapsed;
            this.update({isCollapsed});
            this.dispatchResizeEvent();
            this.setPersistedState({isCollapsed});
            this.track(isCollapsed ? TrackingEvent.CollapsedSidenav : TrackingEvent.ExpandedSidenav);
          } else {
            this.dispatchEvent(new CustomEvent(`footerAction`, {detail: {value}}));
          }
        },

        onHeaderActionClick: (value: string) => {
          this.dispatchEvent(new CustomEvent(`headerAction`, {detail: {value}}));
        },

        onSectionsMouseenter: () => {
          if (this.state.isCollapsed) {
            this.update({isHovering: true});
          }
        },

        onSectionsMouseleave: () => {
          if (this.state.isCollapsed) {
            this.update({isHovering: false});
          }
        },

        isExpandedStyle: () => {
          return !this.state.isCollapsed || this.state.isHovering || this.state.isResizing;
        },

        onItemDrop: (ev) => {
          const {from, to, dropPosition} = ev.detail;
          if (from.itemIdx === to.itemIdx) {
            return;
          }

          let toItemIdx = to.itemIdx;
          if (dropPosition === DropPosition.Above && toItemIdx > from.itemIdx) {
            toItemIdx = Math.max(toItemIdx - 1, 0);
          } else if (dropPosition === DropPosition.Below && toItemIdx < from.itemIdx) {
            toItemIdx += 1;
          }

          this.dispatchEvent(
            new CustomEvent(`dragDrop`, {
              detail: {
                sections: moveSidenavItem(
                  this.attr(`sections`),
                  from.sectionIdx,
                  from.itemIdx,
                  to.sectionIdx,
                  toItemIdx,
                ),
              },
            }),
          );

          this.track(TrackingEvent.Reorder, {
            'section name': this.attr(`sections`)[from.sectionIdx].label,
            'section index': from.sectionIdx,
          });
        },

        onItemDragStart: (ev) => {
          this.update({activeDragSectionIdx: ev.detail.sectionIdx});
        },

        onItemDragEnd: () => {
          this.update({activeDragSectionIdx: null});
        },

        onItemOverflowMenuChange: (ev) => {
          this.update({isOverflowMenuOpen: ev.detail.isOpen});
        },

        onItemOverflowMenuSelect: (ev) => {
          propagateEvent(this, ev);
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.persistence = new Persistence(`sidenav`);
    this.update(this.getPersistedState());
    this.setupMediaQuery();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.collapseMediaQuery.removeListener(this.collapseMediaQueryListener);
  }

  private getMaxAvailableWidth(): number {
    return document.documentElement.clientWidth - this.attr(`min-adjacent-width`);
  }

  private onSidenavResize(initialX: number, initialWidth: number, ev: MouseEvent) {
    ev.stopPropagation();
    ev.preventDefault();
    const delta = ev.clientX - initialX;
    const maxWidth = this.getMaxAvailableWidth();

    let width;
    if (!this.state.isCollapsed && initialWidth + delta > maxWidth) {
      // Cap the width if there's no more room
      width = Math.min(maxWidth, SIDENAV_MAX_WIDTH);
    } else if (Math.abs(initialWidth + delta - SIDENAV_DEFAULT_WIDTH) < SIDENAV_RESIZE_SNAP_WINDOW) {
      // "Snap" to the default width if it's within the window
      width = SIDENAV_DEFAULT_WIDTH;
    } else {
      // Apply min/max bounds
      width = Math.min(Math.max(initialWidth + delta, SIDENAV_MIN_WIDTH), SIDENAV_MAX_WIDTH);
    }

    this.update({width});
    this.dispatchResizeEvent();
  }

  private dispatchResizeEvent() {
    const width = this.state.isCollapsed ? SIDENAV_COLLAPSED_WIDTH : this.state.width;
    this.dispatchEvent(new CustomEvent(`sidenavResize`, {detail: {width}}));
  }

  private getPersistedState(): Partial<SidenavState> {
    return {
      isCollapsed: JSON.parse(this.persistence.get(`isCollapsed`)) || false,
      width: JSON.parse(this.persistence.get(`width`)) || SIDENAV_DEFAULT_WIDTH,
    };
  }

  private setPersistedState(state: Partial<SidenavState>) {
    for (const [key, val] of Object.entries(state)) {
      this.persistence.set(key, val);
    }
  }

  private setupMediaQuery() {
    if (this.collapseMediaQuery) {
      this.collapseMediaQuery.removeListener(this.collapseMediaQueryListener);
    }

    this.collapseMediaQuery = window.matchMedia(
      `screen and (max-width: ${this.attr(`min-adjacent-width`) + SIDENAV_MIN_WIDTH}px)`,
    );

    this.collapseMediaQueryListener = (ev: MediaQueryListEvent) => this.onMediaQueryMatch(ev.matches);
    this.onMediaQueryMatch(this.collapseMediaQuery.matches);
    this.collapseMediaQuery.addListener(this.collapseMediaQueryListener);
  }

  private onMediaQueryMatch(matches: boolean) {
    const state = this.getPersistedState();
    if (matches) {
      this.update({
        isCollapsed: true,
        canExpand: false,
        width: state.width,
        isHovering: false,
      });
    } else {
      this.update({
        isCollapsed: state.isCollapsed,
        canExpand: true,
      });
    }
    this.dispatchResizeEvent();
  }

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: getReportName(),
        ...props,
      },
    });
  }
}

export const SidenavEl = defineAndMakeJsxFactory<SidenavJsxProps>(COMPONENT_NAME, Sidenav);
